import { clinicCalendarDate } from '@/utils/patientJournalPath';

/** Visits that should not be offered as the note's appointment. */
const EXCLUDED_STATUSES = new Set(['cancelled', 'no_show']);

export type JournalDayAppointment = {
  id: string;
  startsAt: string;
  serviceName: string | null;
  clinicianName: string | null;
  status: string;
};

export type JournalLinkPrompt =
  | { kind: 'skip' }
  | { kind: 'one'; appointment: JournalDayAppointment }
  | { kind: 'many'; appointments: JournalDayAppointment[] };

/** YYYY-MM-DD in Europe/Copenhagen. */
export function copenhagenDayKey(iso: string): string {
  return clinicCalendarDate(iso);
}

export function copenhagenTodayKey(now = new Date()): string {
  return clinicCalendarDate(now.toISOString());
}

/** HH:MM in Europe/Copenhagen. */
export function copenhagenClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Copenhagen',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${pick('hour')}:${pick('minute')}`;
}

/**
 * Same Copenhagen day as the note, excluding cancelled and no-show visits.
 * An appointment that already has a journal stays in the list: Ja adds a note.
 */
export function journalAppointmentsOnDay(
  appointments: readonly JournalDayAppointment[],
  noteDay: string,
): JournalDayAppointment[] {
  return appointments
    .filter(
      (appointment) =>
        !EXCLUDED_STATUSES.has(appointment.status) &&
        copenhagenDayKey(appointment.startsAt) === noteDay,
    )
    .sort((left, right) => left.startsAt.localeCompare(right.startsAt));
}

/** Ask only for a new, unlinked note. A stored Nej skips that note. */
export function journalLinkPromptFor(input: {
  alreadyLinked: boolean;
  declined: boolean;
  candidates: readonly JournalDayAppointment[];
}): JournalLinkPrompt {
  if (input.alreadyLinked || input.declined) return { kind: 'skip' };
  if (input.candidates.length === 0) return { kind: 'skip' };
  if (input.candidates.length === 1) {
    return { kind: 'one', appointment: input.candidates[0] };
  }
  return { kind: 'many', appointments: [...input.candidates] };
}

export function journalAppointmentQuestion(appointment: JournalDayAppointment): string {
  const service = appointment.serviceName?.trim() || 'Aftale';
  return `kl. ${copenhagenClock(appointment.startsAt)} · ${service}`;
}

export function journalAppointmentChoice(appointment: JournalDayAppointment): string {
  const service = appointment.serviceName?.trim() || 'Aftale';
  const clinician = appointment.clinicianName?.trim();
  const time = copenhagenClock(appointment.startsAt);
  return clinician ? `${time} · ${service} · ${clinician}` : `${time} · ${service}`;
}
